import axios from "axios";
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { DEFAULT_ERROR_MESSAGE } from "../../constants/generalConstants";
import {
  NETWORK_ERROR_PAGE_ROUTE,
  NOT_FOUND_ERROR_PAGE_ROUTE,
} from "../../constants/routerConstants";
import { useAlert } from "../../hooks/useAlert";
import JwtUtils from "../../router/JwtUtils";
import { AuthContext } from "./TSAuthProvider";

// Create axios instances for default and authentication specific uses
const instance = axios.create({
  baseURL: config.baseURL,
});

const aiChatBotInstance = axios.create({
  baseURL: config.aiChatBotUrl,
});

const AxiosInterceptor = ({ children }) => {
  const { showAlert } = useAlert();

  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const getErrorStatusAndMessage = (error) => {
    const errorStatus =
      error?.response?.data?.status ?? error?.response?.status;
    const errorMessage =
      error?.response?.data?.body ??
      error?.response?.data?.message ??
      error?.response?.data?.error;
    return [errorStatus, errorMessage];
  };

  const handleError = (errorStatus, errorMessage) => {
    if (Boolean(errorStatus)) {
      switch (String(errorStatus)) {
        case "404":
          navigate(NOT_FOUND_ERROR_PAGE_ROUTE);
          break;
        case "401":
          if (!JwtUtils.isTokenValidAndNotExpired()) {
            logout();
          } else {
            showAlert("error", errorMessage ?? DEFAULT_ERROR_MESSAGE);
          }
          break;
        case "451":
          break;
        default:
          showAlert("error", errorMessage ?? DEFAULT_ERROR_MESSAGE);
          break;
      }
    } else {
      navigate(NETWORK_ERROR_PAGE_ROUTE);
    }
  };

  const configuration = (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

  React.useEffect(() => {
    instance.interceptors.request.use(configuration);
    aiChatBotInstance.interceptors.request.use(configuration);

    const responseInterceptor = (response) => {
      if (response.status > 200 && response.status < 300) {
        showAlert("success", response.data?.message || "Operation successful");
      }
      return response;
    };

    const errorInterceptor = (error) => {
      const [errorStatus, errorMessage] = getErrorStatusAndMessage(error);
      handleError(errorStatus, errorMessage);
      return Promise.reject(error);
    };

    instance.interceptors.response.use(responseInterceptor, errorInterceptor);
    aiChatBotInstance.interceptors.response.use(
      responseInterceptor,
      errorInterceptor
    );

    return () => {
      instance.interceptors.request.eject(configuration);
      aiChatBotInstance.interceptors.request.eject(configuration);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};

export { aiChatBotInstance, AxiosInterceptor, instance };

export default instance;
